<template>
  <div class="lad-landing-reviews__custom">
    <div class="reviews-content">
      <h2>{{ $t(`${base}.title`) }}</h2>
      <p class="title-description">{{ $t(`${base}.description`) }}</p>

      <div v-if="getReviews.length > 0" class="custom-carousel">
        <div
          class="carousel-container"
          :style="{
            transform: `translateX(-${currentIndex * slideWidth}px)`,
          }"
        >
          <div
            v-for="review in getReviews"
            :key="review.uuid"
            class="carousel-item"
          >
            <div class="review-item">
              <div class="rate-content">
                <h3>{{ fullname(review._user) }}</h3>

                <el-rate
                  v-model="review.score"
                  disabled
                  text-color="#0A960C"
                ></el-rate>
              </div>

              <p class="review-date">{{
                $dayjs(review.created_at, 'YYYY-MM-DD HH:mm:ss') | ladFnStrDate
              }}</p>

              <div class="review-message">
                <p>{{ review.message }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="getReviews.length > 0" class="carousel-controls">
      <el-button
        icon="el-icon-arrow-left"
        circle
        :disabled="currentIndex === 0"
        @click="prevReview"
      ></el-button>

      <div class="dots">
        <div
          v-for="(item, index) in totalSlides"
          :key="index"
          :class="[`dot`, { active: currentIndex === index }]"
          @click="goToReview(index)"
        ></div>
      </div>

      <el-button
        icon="el-icon-arrow-right"
        circle
        :disabled="currentIndex >= maxIndex"
        @click="nextReview"
      ></el-button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

export default {
  name: 'LadLandingReviewsCustom',
  data() {
    return {
      base: 'landing.reviews',
      loading: false,
      currentIndex: 0,
      slideWidth: 300,
      slidesPerView: 3,
    }
  },
  computed: {
    ...mapState('experience', {
      exExperience: (state) => state.experience,
    }),
    ...mapState('reviews', {
      reviews: 'reviews',
      reviewsSearch: 'search',
    }),
    ...mapGetters('device', ['isMobile']),
    reviewsList() {
      return this.reviewsSearch
        ? this.reviews.filter((review) => {
            const message = review.message.removeDiacritical()
            const search = this.reviewsSearch.removeDiacritical()

            return message.includes(search)
          })
        : this.reviews.filter((review) => review.message)
    },
    getReviews() {
      return this.reviewsList.slice(0, 8)
    },
    totalSlides() {
      if (this.isMobile) {
        return this.getReviews.length
      }
      return Math.max(1, this.getReviews.length - this.slidesPerView + 1)
    },
    maxIndex() {
      if (this.isMobile) {
        return this.getReviews.length - 1
      }
      return Math.max(0, this.getReviews.length - this.slidesPerView)
    },
  },
  watch: {
    getReviews() {
      this.$nextTick(() => {
        this.updateSlideWidth()
      })
    },
  },
  mounted() {
    this.init()
    this.$nextTick(() => {
      this.updateSlideWidth()
    })
    window.addEventListener('resize', this.updateSlideWidth)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateSlideWidth)
  },
  methods: {
    async init() {
      this.loading = true

      const firstReview = this.$route.query.first_review || ''
      const data = {
        product: { uuid: this.exExperience.uuid },
        firstReview: { uuid: firstReview },
      }

      try {
        await this.$store.dispatch('reviews/getProductReviews', data)
      } catch (error) {}

      this.loading = false
    },
    fullname(_user) {
      const firstname = _user.profile.firstname ? _user.profile.firstname : ''
      const lastname = _user.profile.lastname ? _user.profile.lastname : ''

      return `${firstname} ${lastname}`.trim()
    },
    updateSlideWidth() {
      // Get the actual width of the carousel container
      const carouselElement = this.$el?.querySelector('.custom-carousel')
      if (carouselElement) {
        const containerWidth = carouselElement.offsetWidth

        if (this.isMobile) {
          // Mobile: 1 item takes 100% width
          const itemWidth = containerWidth

          // Set the width for each carousel item
          const carouselItems = carouselElement.querySelectorAll(
            '.carousel-item'
          )
          carouselItems.forEach((item) => {
            item.style.width = `${itemWidth}px`
          })

          this.slideWidth = itemWidth
          this.slidesPerView = 1
        } else {
          // Desktop: 3 items with gaps
          const gap = 32 // Gap between items
          const totalGaps = 2 // 2 gaps for 3 items
          const availableWidth = containerWidth - gap * totalGaps
          const itemWidth = availableWidth / 3

          // Set the width for each carousel item
          const carouselItems = carouselElement.querySelectorAll(
            '.carousel-item'
          )
          carouselItems.forEach((item) => {
            item.style.width = `${itemWidth}px`
          })

          this.slideWidth = itemWidth + gap
          this.slidesPerView = 3
        }
      } else if (this.isMobile) {
        // Fallback calculation for mobile
        this.slideWidth = window.innerWidth - 40
        this.slidesPerView = 1
      } else {
        // Fallback calculation for desktop
        this.slideWidth = 350
        this.slidesPerView = 3
      }
    },
    nextReview() {
      if (this.currentIndex < this.maxIndex) {
        this.currentIndex++
      }
    },
    prevReview() {
      if (this.currentIndex > 0) {
        this.currentIndex--
      }
    },
    goToReview(index) {
      this.currentIndex = index
    },
  },
}
</script>

<style lang="scss">
.lad-landing-reviews {
  &__custom {
    padding: 20px;

    h2 {
      font-size: 20px;
      font-weight: 700;
      line-height: 22px;
      color: #221f20;
      margin: 0 0 10px;

      @media (min-width: 768px) {
        font-size: 32px;
        margin: 0 0 25px;
      }
    }

    p {
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
      color: #221f20;
      margin: 0 0 15px;

      @media (min-width: 768px) {
        font-size: 20px;
      }
    }

    .title-description {
      margin: 0 0 20px;

      @media (min-width: 768px) {
        margin: 0 0 35px;
      }
    }

    .reviews-content {
      padding: 32px 20px;
      border-radius: 6px;
      background-color: #fff;
      margin: 0 0 25px;

      @media (min-width: 768px) {
        margin: 0 0 15px;
      }

      .custom-carousel {
        width: 100%;
        margin: 0 0 30px;
        overflow: hidden;
        position: relative;

        .carousel-container {
          display: flex;
          transition: transform 0.3s ease;
          width: fit-content;
          gap: 0;

          .carousel-item {
            flex-shrink: 0;
            box-sizing: border-box;

            .review-item {
              border-radius: 8px;
              background-color: #fff;
              height: 100%;
              width: 100%;

              @media (min-width: 768px) {
                padding: 40px 24px;
              }

              .rate-content {
                display: grid;
                grid-template-columns: auto max-content;
                gap: 10px;
                margin: 0 0 10px;

                h3 {
                  font-size: 18px;
                  font-weight: 700;
                  line-height: 21.6px;
                  color: #0a960c;
                  margin: 0;
                }

                .el-rate__icon {
                  font-size: 24px;
                  color: #0a960c !important;
                }
              }

              .review-message {
                max-height: 120px;
                overflow-y: auto;
                scrollbar-width: none; /* Firefox */
                -ms-overflow-style: none; /* IE and Edge */

                &::-webkit-scrollbar {
                  display: none; /* Chrome, Safari and Opera */
                }

                p {
                  font-size: 16px;
                  margin: 0;
                }
              }

              p {
                margin: 0;

                &.review-date {
                  font-size: 14px;
                  font-style: italic;
                  font-weight: 400;
                  line-height: 21px;
                  margin: 0 0 10px;
                }
              }
            }
          }
        }
      }

      @media (min-width: 768px) {
        background-color: #ebeef2;
        padding: 48px 40px 0;

        .custom-carousel {
          .carousel-container {
            gap: 32px;
          }
        }
      }
    }

    .carousel-controls {
      display: grid;
      grid-template-columns: 24px 1fr 24px;
      gap: 10px;
      align-items: center;

      .el-button {
        width: 24px;
        height: 24px;
        padding: 0;
        border: unset;
        background: unset;
        color: #1d1d1d;

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        i {
          font-size: 16px;
          font-weight: 600;
        }
      }

      .dots {
        display: flex;
        max-width: 200px;
        gap: 10px;
        justify-self: center;

        .dot {
          display: inline-flex;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          border: 1px solid #b2b1b1;
          cursor: pointer;
          transition: all 0.3s ease;

          &:hover {
            background-color: #e0e0e0;
          }

          &.active {
            border: unset;
            background-color: #0a960c;
          }
        }
      }

      @media screen and (min-width: 768px) {
        display: flex;
        justify-content: center;
        margin-bottom: 40px;
      }
    }
  }
}
</style>
